'use client';

import {ExpressiveIcon} from '@udemy-v2/icons-expressive';
import type {ExpressiveIconName} from '@udemy-v2/icons-expressive';
import {useI18n} from '@udemy-v2/i18n';

import type {SkillApiItem} from '@/features/instructor-profile/lib/types';

import styles from './skills.module.scss';

interface SkillsProps {
    /**
     * Skills to display. Presentational only — this component never fetches; the
     * public profile page and (later) the instructor preview tab both pass
     * already-resolved data here. `null`/`undefined`/empty renders nothing.
     */
    skills?: SkillApiItem[] | null;
}

/**
 * Public, display-only "Skills I teach" card (Figma node 2944:39937).
 *
 * A white rounded card that overlaps the bottom of the {@link ProfileHeader} and
 * is horizontally centred. Each skill is a read-only icon + label — the icon is
 * the same `ExpressiveIcon` used by the editable instructor tooling
 * (`src/features/instructor-profile/components/skills-section`), with all
 * interactivity (Autosuggest, MultiSelect, FormGroup, remove buttons) stripped.
 */
export function Skills({skills}: SkillsProps) {
    const {gettext} = useI18n();

    if (!skills || skills.length === 0) {
        return null;
    }

    return (
        <section className={styles['skills']} aria-labelledby="skills-heading">
            <div className={styles['card']}>
                <h2 id="skills-heading" className={styles['heading']}>
                    {gettext('Skills I teach')}
                </h2>
                <ul
                    className={styles['list']}
                    // Drives the mobile row layout: 4 skills balance to 2×2
                    // instead of 3+1 (see skills.module.scss).
                    data-count={skills.length}
                >
                    {skills.map((skill, index) => (
                        <li
                            // Skill titles can repeat, so pair them with the index for a stable key.
                            key={`${skill.skill_title}-${index}`}
                            className={styles['skill']}
                        >
                            <span className={styles['icon-circle']}>
                                <ExpressiveIcon
                                    name={skill.icon_title as ExpressiveIconName}
                                    className={styles['icon']}
                                />
                            </span>
                            <span className={styles['label']}>{skill.skill_title}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}
